interface HijriDate {
  day: string;
  month: {
    number: number;
    en: string;
    ar: string;
  };
  year: string;
  designation: {
    abbreviated: string;
    expanded: string;
  };
  holidays: string[];
}

interface GregorianDate {
  day: string;
  month: {
    number: number;
    en: string;
  };
  year: string;
  date: string;
}

interface CalendarResponse {
  data: {
    hijri: HijriDate;
    gregorian: GregorianDate;
  };
}

const API_BASE = 'https://api.aladhan.com/v1';

export async function convertHijriToGregorian(hijriDay: number, hijriMonth: number, hijriYear: number): Promise<Date | null> {
  try {
    const response = await fetch(`${API_BASE}/hToG/${hijriDay}-${hijriMonth}-${hijriYear}`);
    const data: CalendarResponse = await response.json();

    if (data.data && data.data.gregorian) {
      const dateStr = data.data.gregorian.date;
      const [day, month, year] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    }
    return null;
  } catch (error) {
    console.error('Error converting Hijri to Gregorian:', error);
    return null;
  }
}

export async function getCurrentHijriYear(): Promise<number> {
  try {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();

    const response = await fetch(`${API_BASE}/gToH/${dd}-${mm}-${yyyy}`);
    const data: CalendarResponse = await response.json();

    return parseInt(data.data.hijri.year);
  } catch (error) {
    console.error('Error getting current Hijri year:', error);
    return 1447;
  }
}

interface IslamicEventDefinition {
  id: string;
  name: string;
  nameArabic: string;
  hijriDay: number;
  hijriMonth: number;
  type: 'eid' | 'ramadan' | 'hajj' | 'special';
  description: string;
}

const ISLAMIC_EVENT_DEFINITIONS: IslamicEventDefinition[] = [
  {
    id: 'islamic-new-year',
    name: 'Islamic New Year',
    nameArabic: 'رأس السنة الهجرية',
    hijriDay: 1,
    hijriMonth: 1,
    type: 'special',
    description: 'First of Muharram - New Hijri year'
  },
  {
    id: 'ashura',
    name: 'Day of Ashura',
    nameArabic: 'يوم عاشوراء',
    hijriDay: 10,
    hijriMonth: 1,
    type: 'special',
    description: '10th of Muharram - Fast recommended'
  },
  {
    id: 'mawlid',
    name: 'Mawlid al-Nabi',
    nameArabic: 'المولد النبوي الشريف',
    hijriDay: 12,
    hijriMonth: 3,
    type: 'special',
    description: 'Birthday of Prophet Muhammad (ﷺ)'
  },
  {
    id: 'isra-miraj',
    name: "Isra and Mi'raj",
    nameArabic: 'الإسراء والمعراج',
    hijriDay: 27,
    hijriMonth: 7,
    type: 'special',
    description: 'Night Journey and Ascension'
  },
  {
    id: 'shaban-15',
    name: "Laylat al-Bara'ah",
    nameArabic: 'ليلة البراءة',
    hijriDay: 15,
    hijriMonth: 8,
    type: 'special',
    description: "Night of Forgiveness (15th Sha'ban)"
  },
  {
    id: 'ramadan-start',
    name: 'Ramadan Begins',
    nameArabic: 'بداية رمضان',
    hijriDay: 1,
    hijriMonth: 9,
    type: 'ramadan',
    description: 'The blessed month of fasting begins'
  },
  {
    id: 'ramadan-10',
    name: 'First Third of Ramadan',
    nameArabic: 'الثلث الأول من رمضان',
    hijriDay: 10,
    hijriMonth: 9,
    type: 'ramadan',
    description: 'Days of Mercy'
  },
  {
    id: 'ramadan-15',
    name: 'Middle of Ramadan',
    nameArabic: 'منتصف رمضان',
    hijriDay: 15,
    hijriMonth: 9,
    type: 'ramadan',
    description: '15th night of Ramadan'
  },
  {
    id: 'ramadan-20',
    name: 'Last Third of Ramadan',
    nameArabic: 'الثلث الأخير من رمضان',
    hijriDay: 20,
    hijriMonth: 9,
    type: 'ramadan',
    description: 'Days of Freedom from Hellfire'
  },
  {
    id: 'ramadan-last10',
    name: 'Last 10 Nights Begin',
    nameArabic: 'بداية العشر الأواخر',
    hijriDay: 21,
    hijriMonth: 9,
    type: 'ramadan',
    description: 'Most blessed nights of Ramadan'
  },
  {
    id: 'laylat-qadr-21',
    name: 'Laylat al-Qadr (21st)',
    nameArabic: 'ليلة القدر ٢١',
    hijriDay: 21,
    hijriMonth: 9,
    type: 'special',
    description: 'Night of Power - Possible date'
  },
  {
    id: 'laylat-qadr-23',
    name: 'Laylat al-Qadr (23rd)',
    nameArabic: 'ليلة القدر ٢٣',
    hijriDay: 23,
    hijriMonth: 9,
    type: 'special',
    description: 'Night of Power - Possible date'
  },
  {
    id: 'laylat-qadr-25',
    name: 'Laylat al-Qadr (25th)',
    nameArabic: 'ليلة القدر ٢٥',
    hijriDay: 25,
    hijriMonth: 9,
    type: 'special',
    description: 'Night of Power - Possible date'
  },
  {
    id: 'laylat-qadr-27',
    name: 'Laylat al-Qadr (27th)',
    nameArabic: 'ليلة القدر ٢٧',
    hijriDay: 27,
    hijriMonth: 9,
    type: 'special',
    description: 'Night of Power - Most likely date'
  },
  {
    id: 'laylat-qadr-29',
    name: 'Laylat al-Qadr (29th)',
    nameArabic: 'ليلة القدر ٢٩',
    hijriDay: 29,
    hijriMonth: 9,
    type: 'special',
    description: 'Night of Power - Possible date'
  },
  {
    id: 'eid-fitr',
    name: 'Eid al-Fitr',
    nameArabic: 'عيد الفطر',
    hijriDay: 1,
    hijriMonth: 10,
    type: 'eid',
    description: 'Festival marking the end of Ramadan'
  },
  {
    id: 'eid-fitr-2',
    name: 'Eid al-Fitr (Day 2)',
    nameArabic: 'عيد الفطر - اليوم الثاني',
    hijriDay: 2,
    hijriMonth: 10,
    type: 'eid',
    description: 'Second day of Eid celebrations'
  },
  {
    id: 'eid-fitr-3',
    name: 'Eid al-Fitr (Day 3)',
    nameArabic: 'عيد الفطر - اليوم الثالث',
    hijriDay: 3,
    hijriMonth: 10,
    type: 'eid',
    description: 'Third day of Eid celebrations'
  },
  {
    id: 'dhul-hijjah-start',
    name: 'Start of Dhul Hijjah',
    nameArabic: 'بداية ذو الحجة',
    hijriDay: 1,
    hijriMonth: 12,
    type: 'hajj',
    description: 'The sacred month of Hajj begins'
  },
  {
    id: 'dhul-hijjah-first10',
    name: 'First 10 Days of Dhul Hijjah',
    nameArabic: 'عشر ذي الحجة',
    hijriDay: 1,
    hijriMonth: 12,
    type: 'hajj',
    description: 'Most blessed days of the year'
  },
  {
    id: 'tarwiyah',
    name: 'Day of Tarwiyah',
    nameArabic: 'يوم التروية',
    hijriDay: 8,
    hijriMonth: 12,
    type: 'hajj',
    description: '8th of Dhul Hijjah - Hajj begins'
  },
  {
    id: 'arafah',
    name: 'Day of Arafah',
    nameArabic: 'يوم عرفة',
    hijriDay: 9,
    hijriMonth: 12,
    type: 'hajj',
    description: '9th of Dhul Hijjah - The most important day of Hajj'
  },
  {
    id: 'eid-adha',
    name: 'Eid al-Adha',
    nameArabic: 'عيد الأضحى',
    hijriDay: 10,
    hijriMonth: 12,
    type: 'eid',
    description: 'Festival of Sacrifice - 10th of Dhul Hijjah'
  },
  {
    id: 'eid-adha-2',
    name: 'Eid al-Adha (Day 2)',
    nameArabic: 'عيد الأضحى - اليوم الثاني',
    hijriDay: 11,
    hijriMonth: 12,
    type: 'eid',
    description: 'Second day of Eid al-Adha - Days of Tashriq'
  },
  {
    id: 'eid-adha-3',
    name: 'Eid al-Adha (Day 3)',
    nameArabic: 'عيد الأضحى - اليوم الثالث',
    hijriDay: 12,
    hijriMonth: 12,
    type: 'eid',
    description: 'Third day of Eid al-Adha - Days of Tashriq'
  },
  {
    id: 'eid-adha-4',
    name: 'Eid al-Adha (Day 4)',
    nameArabic: 'عيد الأضحى - اليوم الرابع',
    hijriDay: 13,
    hijriMonth: 12,
    type: 'eid',
    description: 'Fourth day of Eid al-Adha - Days of Tashriq'
  }
];

export async function fetchIslamicEventsForYear(): Promise<Array<{
  id: string;
  name: string;
  nameArabic: string;
  date: Date;
  type: 'eid' | 'ramadan' | 'hajj' | 'special';
  description: string;
  hijriDate: string;
}>> {
  try {
    const currentHijriYear = await getCurrentHijriYear();
    const events = [];

    for (const hijriYear of [currentHijriYear, currentHijriYear + 1]) {
      for (const eventDef of ISLAMIC_EVENT_DEFINITIONS) {
        const gregorianDate = await convertHijriToGregorian(
          eventDef.hijriDay,
          eventDef.hijriMonth,
          hijriYear
        );

        if (gregorianDate) {
          const now = new Date();
          const twoYearsAgo = new Date(now.getFullYear() - 2, 0, 1);
          const twoYearsFromNow = new Date(now.getFullYear() + 2, 11, 31);

          if (gregorianDate >= twoYearsAgo && gregorianDate <= twoYearsFromNow) {
            events.push({
              ...eventDef,
              id: `${eventDef.id}-${hijriYear}`,
              date: gregorianDate,
              hijriDate: `${eventDef.hijriDay} ${getHijriMonthName(eventDef.hijriMonth)} ${hijriYear}`
            });
          }
        }
      }
    }

    return events.sort((a, b) => a.date.getTime() - b.date.getTime());
  } catch (error) {
    console.error('Error fetching Islamic events:', error);
    return [];
  }
}

function getHijriMonthName(monthNumber: number): string {
  const months = [
    'Muharram', 'Safar', 'Rabi I', 'Rabi II', 'Jumada I', 'Jumada II',
    'Rajab', "Sha'ban", 'Ramadan', 'Shawwal', "Dhu al-Qi'dah", 'Dhu al-Hijjah'
  ];
  return months[monthNumber - 1] || '';
}
